/**
 * Utility functions to send notifications via Courier API when status >= 400
 */

import { store } from '@/core/store/store'

interface CourierErrorNotificationParams {
  userId: string
  errorMessage: string
  errorTitle?: string
  statusCode: number
}

/**
 * Transforms error codes in message to markdown bold code format
 * Detects patterns like COM_TKP_LIM-CUZ_PAX_EXT and wraps them as **`pattern`**
 * @param message - Original error message
 * @returns Message with formatted error codes
 */
const formatErrorCodesInMessage = (message: string): string => {
  // Regex pattern to match codes like: COM_TKP_LIM-CUZ_PAX_EXT
  // Safer pattern that avoids catastrophic backtracking
  // Matches: Letter + segments separated by _ or - with at least one _
  const codePattern = /\b([A-Z]+(?:_[A-Z0-9]+)+(?:-[A-Z0-9]+)*)\b/g

  return message.replaceAll(codePattern, '**`$1`**')
}

/**
 * Sends a notification via Courier API for error cases
 * @param params - Notification parameters object
 */
export const sendCourierErrorNotification = async (params: CourierErrorNotificationParams): Promise<void> => {
  const { userId, errorMessage, errorTitle = 'Error en la aplicación', statusCode } = params

  // Solo enviar notificaciones para errores >= 400
  if (statusCode < 400) {
    return
  }

  try {
    const notificationData = {
      userId,
      title: errorTitle,
      message: formatErrorCodesInMessage(errorMessage),
      data: {
        statusCode,
        timestamp: new Date().toISOString(),
      },
    }

    const response = await fetch('/api/notifications/send', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(notificationData),
    })

    if (!response.ok) {
      // Si falla el envío, no interferir con el flujo principal
      // Solo log silencioso en desarrollo
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to send Courier notification:', await response.text())
      }
    }
  } catch (error) {
    // Manejo silencioso de errores
    // No queremos que fallen las notificaciones de Courier rompan el flujo
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error sending Courier notification:', error)
    }
  }
}

/**
 * Helper function to get current user ID from Redux store
 */
export const getCurrentUserId = (): string | null => {
  try {
    // Dynamic import to avoid SSR issues
    const state = store.getState()

    // Get nombreUsuario from UserReducer (which is the email/userId)
    const nombreUsuario = state.UserReducer?.nombreUsuario

    return nombreUsuario || null
  } catch {
    return null
  }
}
